import { addDbItem, getDbItem } from "./idb-util"
import { compress, decompress, encrypt, decrypt } from "./cache-utils"
import { getObjectId } from "../comp/Utils"

/**
 * Per-person IndexedDB cache used by the time tracking grid for the slow-moving
 * reference data (missions, clients). Timesheets, roles and leaves are never
 * cached: they decide what is shown and what is editable and must be fresh.
 *
 * Record shape (one record per person, keyed by person id):
 *   { id, v, syncedAt, memberIds?, items: <encrypted compressed array> }
 *
 * Any problem reading a record (missing, wrong version, too old, key changed,
 * corrupt) is treated as a cache miss so the caller falls back to the full fetch.
 */

export const TT_MISSIONS_DB = "ttMyMissions"
export const TT_CLIENTS_DB = "ttMyClients"
export const TT_WEEK_HOURS_DB = "ttMyWeekHours"

// Bump when the cached document shape changes (e.g. a new field in the select
// list). Old records are then ignored and rebuilt from a full fetch.
export const TT_CACHE_VERSION = 1

// Records older than this are discarded and rebuilt from scratch, so any drift
// the delta sync cannot see (see reconcileMissions) is bounded in time.
export const TT_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

// The delta query is `updatedAt gte syncedAt`. Server and browser clocks are not
// the same clock, so the stamp is taken this far in the past. Re-fetching a few
// minutes of already-seen changes is free; missing a change is not.
export const TT_CACHE_SYNC_SKEW_MS = 10 * 60 * 1000

// A cache synced this recently is used as-is: no delta request at all. Week
// flipping is the most common action in the grid and each flip would otherwise
// cost a scope resolution plus an Atlas Search op on the server just to learn
// that nothing changed. Roles, timesheets, leaves and membership are still
// fetched fresh on every load, so this only delays seeing a mission or client
// rename by at most this long.
export const TT_CACHE_FRESH_MS = 5 * 60 * 1000

export function getTtCacheSyncStamp(now = Date.now()) {
    return new Date(now - TT_CACHE_SYNC_SKEW_MS).toISOString()
}

/**
 * True when the cache's last full sync happened less than TT_CACHE_FRESH_MS ago.
 * The stored stamp is already skewed into the past by TT_CACHE_SYNC_SKEW_MS, so
 * that is added back to get the wall-clock time of the sync.
 */
export function isTtCacheFresh(cache, now = Date.now()) {
    if (!cache?.syncedAt) {
        return false
    }

    const syncedAtMs = new Date(cache.syncedAt).getTime()

    if (!Number.isFinite(syncedAtMs)) {
        return false
    }

    return now - (syncedAtMs + TT_CACHE_SYNC_SKEW_MS) < TT_CACHE_FRESH_MS
}

export async function readTtCache({ dbName, uid, encryptionKey, now = Date.now(), maxAgeMs = TT_CACHE_MAX_AGE_MS }) {
    if (!uid || !encryptionKey) {
        return null
    }

    try {
        const record = await getDbItem(dbName, "store", uid)

        if (!record || record.v !== TT_CACHE_VERSION || !record.syncedAt || !record.items) {
            return null
        }

        const syncedAtMs = new Date(record.syncedAt).getTime()

        if (!Number.isFinite(syncedAtMs) || now - syncedAtMs > maxAgeMs) {
            return null
        }

        const decrypted = await decrypt(encryptionKey, record.items)

        if (!decrypted) {
            return null
        }

        const items = decompress(decrypted)

        if (!Array.isArray(items)) {
            return null
        }

        return {
            syncedAt: record.syncedAt,
            items,
            memberIds: Array.isArray(record.memberIds) ? record.memberIds : null,
        }
    } catch (err) {
        console.warn(`tt-cache: read failed for ${dbName}`, err)
        return null
    }
}

export async function writeTtCache({ dbName, uid, encryptionKey, syncedAt, items, memberIds }) {
    if (!uid || !encryptionKey || !syncedAt || !Array.isArray(items)) {
        return
    }

    try {
        const encrypted = await encrypt(encryptionKey, compress(items))

        await addDbItem(dbName, "store", {
            id: uid,
            v: TT_CACHE_VERSION,
            syncedAt,
            memberIds: Array.isArray(memberIds) ? memberIds : undefined,
            items: encrypted,
        })
    } catch (err) {
        console.warn(`tt-cache: write failed for ${dbName}`, err)
    }
}

/**
 * Index a list of documents by _id. Later lists win over earlier ones, so pass
 * the freshest data last. An already-indexed map (the output of a previous
 * call) is accepted too, so results can be merged with fresh lists.
 */
export function indexById(...lists) {
    const map = {}

    lists.forEach((list) => {
        const docs = Array.isArray(list) ? list : list && typeof list === "object" ? Object.values(list) : []

        docs.forEach((doc) => {
            const id = getObjectId(doc?._id)

            if (id) {
                map[id] = doc
            }
        })
    })

    return map
}

/**
 * Work out which mission ids still have to be fetched by id, given what the
 * cache plus the delta already know.
 *
 * A mission is fetched by id when:
 *  - it is not known at all, or
 *  - the person was not a member of it at the last sync. A mission can be edited
 *    while the person is off it (the `participating` delta cannot see those
 *    edits), so a re-join must refresh the document even if `updatedAt` is old.
 *
 * `previousMemberIds === null` means the cache has no membership snapshot, in
 * which case every needed mission is refreshed.
 */
export function getMissionIdsToFetch({ neededIds, known, previousMemberIds }) {
    const previous = previousMemberIds ? new Set(previousMemberIds) : null

    return (neededIds || []).filter((id) => {
        if (!known[id]) {
            return true
        }

        if (!previous) {
            return true
        }

        return !previous.has(id)
    })
}

/**
 * Pick the missions the grid should build rows for.
 *
 * `neededIds` come from the fresh roles; `known` is cache + delta + by-id fetch;
 * `memberIds` is the fresh membership list. Only missions the person is
 * currently a member of are returned, which is exactly what the previous
 * `participating: true` by-id fetch enforced server-side. The result is ordered
 * by _id descending to match the API's default sort.
 */
export function reconcileMissions({ neededIds, known, memberIds }) {
    const members = new Set((memberIds || []).map((id) => getObjectId(id)))

    return (neededIds || [])
        .map((id) => known[id])
        .filter((m) => m && members.has(getObjectId(m._id)))
        .sort((a, b) =>
            getObjectId(a._id) < getObjectId(b._id) ? 1 : getObjectId(a._id) > getObjectId(b._id) ? -1 : 0,
        )
}

/*
 * Last-week hours used to rank missions.
 *
 * When the current week has no project hours yet, missions are ordered by how
 * many hours they got the week before. That used to cost a separate timesheet
 * request on every load. Instead, whenever a week is on screen its per-mission
 * hours are remembered here, so loading the following week finds them locally.
 * A week that was never on screen still falls back to the request.
 */

// The fallback request only returns the 20 newest of last week's sheets. The
// cached ranking applies the same cut so the order is identical whichever path
// served it. Drop this together with the request's limit if the ranking should
// ever consider every sheet.
export const TT_LAST_WEEK_RANK_LIMIT = 20

// Week entries older than this are pruned; the record itself is refreshed on
// every load, so this is also how long the store survives without a visit.
export const TT_WEEK_HOURS_MAX_AGE_MS = 70 * 24 * 60 * 60 * 1000

/**
 * Per-mission hours for a week's timesheets, matching what the fallback request
 * returns: project sheets that have at least one real entry, newest 20 by _id.
 * `getHours` is the app's timesheet hours function (injected so this module
 * stays free of grid dependencies).
 */
export function rankHoursByMission(timesheets, getHours, limit = TT_LAST_WEEK_RANK_LIMIT) {
    const eligible = (timesheets || [])
        .filter(
            (ts) =>
                ts &&
                ts.type === "project" &&
                Array.isArray(ts.actionItems) &&
                ts.actionItems.some((ai) => ai?.actionItemId),
        )
        .sort((a, b) => {
            const ai = getObjectId(a._id) || ""
            const bi = getObjectId(b._id) || ""
            return ai < bi ? 1 : ai > bi ? -1 : 0
        })
        .slice(0, limit)

    const byMission = {}

    eligible.forEach((ts) => {
        const missionId = getObjectId(ts.mission)

        if (!missionId) {
            return
        }

        if (!byMission[missionId]) {
            byMission[missionId] = []
        }

        byMission[missionId].push(ts)
    })

    const hours = {}

    Object.keys(byMission).forEach((missionId) => {
        hours[missionId] = getHours(byMission[missionId]) || 0
    })

    return hours
}

/**
 * The timesheets currently on screen, overlaid on the ones fetched for the
 * week. Rows carry the edited copy of a sheet; the fetched list also holds
 * sheets for missions that are not rendered (locked roles), which the fallback
 * request would still count. Later wins, so pass the rows last.
 */
export function collectWeekTimesheets(fetched, rows) {
    const byId = indexById(fetched)

    ;(rows || []).forEach((row) => {
        const ts = row?.timesheet
        const id = getObjectId(ts?._id)

        if (id) {
            byId[id] = ts
        }
    })

    return Object.values(byId)
}

export function findWeekHours(items, weekStart) {
    const key = String(weekStart)
    const entry = (items || []).find((e) => e && e._id === key)

    return entry && entry.hours && typeof entry.hours === "object" ? entry.hours : null
}

export function mergeWeekHours(items, weekStart, hours, now = Date.now()) {
    const key = String(weekStart)

    const kept = (items || []).filter(
        (e) => e && e._id !== key && Number.isFinite(e.at) && now - e.at < TT_WEEK_HOURS_MAX_AGE_MS,
    )

    return [...kept, { _id: key, hours: hours || {}, at: now }]
}

// Read-merge-write is serialised so a load and the on-screen persist can never
// overwrite each other's entry.
let weekHoursQueue = Promise.resolve()

export function upsertWeekHours({ uid, encryptionKey, weekStart, hours }) {
    const run = async () => {
        if (!uid || !encryptionKey || !Number.isFinite(weekStart)) {
            return
        }

        const cache = await readTtCache({
            dbName: TT_WEEK_HOURS_DB,
            uid,
            encryptionKey,
            maxAgeMs: TT_WEEK_HOURS_MAX_AGE_MS,
        })

        await writeTtCache({
            dbName: TT_WEEK_HOURS_DB,
            uid,
            encryptionKey,
            syncedAt: new Date().toISOString(),
            items: mergeWeekHours(cache?.items, weekStart, hours),
        })
    }

    weekHoursQueue = weekHoursQueue.then(run, run)

    return weekHoursQueue
}
